import { ed, svgText, niceMax } from "./tokens";
import { useTooltip } from "./useTooltip";

export interface SlopeSeries {
  id: string;
  label: string;
  from: number | null;
  to: number | null;
  tone?: "emph" | "caution";
}

interface SlopeChartProps {
  series: SlopeSeries[];
  fromLabel: string;
  toLabel: string;
  format?: (v: number) => string;
  ariaLabel: string;
}

const W = 460;
const H = 230;
const M = { t: 18, r: 170, b: 28, l: 170 };

/** Separate labels that would overlap, keeping their order. */
function spread(ys: number[], gap = 14): number[] {
  const order = ys.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
  for (let k = 1; k < order.length; k++) {
    if (order[k]!.y - order[k - 1]!.y < gap) order[k]!.y = order[k - 1]!.y + gap;
  }
  const out = [...ys];
  for (const o of order) out[o.i] = o.y;
  return out;
}

/** Two-point change for a handful of series on one shared scale. */
export function SlopeChart({
  series,
  fromLabel,
  toLabel,
  format = (v) => String(Math.round(v)),
  ariaLabel,
}: SlopeChartProps) {
  const { bind, node } = useTooltip();
  const drawn = series.filter((s) => s.from !== null && s.to !== null);
  const max = niceMax(Math.max(1, ...drawn.flatMap((s) => [s.from!, s.to!])));
  const y = (v: number) => M.t + (H - M.t - M.b) * (1 - v / max);
  const x0 = M.l;
  const x1 = W - M.r;
  const leftY = spread(drawn.map((s) => y(s.from!) + 4));
  const rightY = spread(drawn.map((s) => y(s.to!) + 4));

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W }} role="img" aria-label={ariaLabel}>
        {[
          [fromLabel, x0],
          [toLabel, x1],
        ].map(([t, xx]) => (
          <g key={String(t)}>
            <line x1={xx} x2={xx} y1={M.t} y2={H - M.b} stroke={ed.rule} />
            <text
              x={xx}
              y={H - 8}
              textAnchor="middle"
              style={{ ...svgText.label, fontWeight: 700, fill: ed.ink }}
            >
              {t}
            </text>
          </g>
        ))}
        {drawn.map((s, i) => {
          const color =
            s.tone === "emph" ? ed.emph : s.tone === "caution" ? ed.caution : ed.markStrong;
          return (
            <g
              key={s.id}
              {...bind(
                <>
                  <b>{s.label}</b>
                  <span className="num">{format(s.from!)}</span> →{" "}
                  <span className="num">{format(s.to!)}</span>
                </>,
              )}
            >
              <line
                x1={x0}
                x2={x1}
                y1={y(s.from!)}
                y2={y(s.to!)}
                stroke={color}
                strokeWidth={s.tone ? 2.5 : 1.5}
              />
              <circle cx={x0} cy={y(s.from!)} r={4} fill={ed.paper} stroke={color} strokeWidth={1.5} />
              <circle cx={x1} cy={y(s.to!)} r={4.5} fill={color} />
              <text x={x0 - 10} y={leftY[i]} textAnchor="end" style={svgText.label}>
                {s.label}{" "}
                <tspan style={svgText.num}>{format(s.from!)}</tspan>
              </text>
              <text x={x1 + 10} y={rightY[i]} style={s.tone ? svgText.strong : svgText.label}>
                <tspan style={s.tone ? svgText.numStrong : svgText.num}>
                  {format(s.to!)}
                </tspan>{" "}
                {s.label}
              </text>
            </g>
          );
        })}
      </svg>
      {node}
    </>
  );
}
